import React, { useEffect, useState } from 'react';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import './Classroom.css';
import { useTranslation } from 'react-i18next';
import { Button, Typography, Spin, Table, message, Tooltip, Tabs, Select } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;
const { Option } = Select;

function Classroom() {
  const { t, i18n } = useTranslation();
  const [classrooms, setClassrooms] = useState([]);
  const [classes, setClasses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userBranchId, setUserBranchId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const API_URL = process.env.REACT_APP_API_URL;

  // Get user info from token or localStorage
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserRole(payload.role);
        setUserBranchId(payload.branch_id || payload.branchId);
        
        // For non-superadmin users, set their branch as selected by default
        if (payload.role !== 'superadmin') {
          setSelectedBranch(payload.branch_id || payload.branchId);
        }
      } catch (error) {
        console.error('Error parsing token:', error);
      }
    }
  }, []);

  // Fetch branches for superadmin
  useEffect(() => {
    const fetchBranches = async () => {
      if (userRole === 'superadmin') {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API_URL}/branch`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setBranches(response.data);
          
          // Set first branch as default if none selected
          if (!selectedBranch && response.data.length > 0) {
            setSelectedBranch(1);
          }
        } catch (error) {
          console.error('Error fetching branches:', error);
          message.error('Failed to fetch branches');
        }
      }
    };

    if (userRole) {
      fetchBranches();
    }
  }, [userRole, API_URL, selectedBranch]);

  // Generate 30-minute time slots from 9:00 AM to 8:00 PM
  const generateTimeSlots = () => {
    const slots = [];
    const startHour = 9; // 9 AM
    const endHour = 20; // 8 PM
    
    for (let hour = startHour; hour < endHour; hour++) {
      // First half hour (e.g., 9:00-9:30)
      slots.push({
        key: `${hour}:00`,
        display: `${hour.toString().padStart(2, '0')}:00 - ${hour.toString().padStart(2, '0')}:30`,
        startTime: `${hour.toString().padStart(2, '0')}:00`,
        endTime: `${hour.toString().padStart(2, '0')}:30`
      });
      
      // Second half hour (e.g., 9:30-10:00)
      const nextHour = hour + 1;
      slots.push({
        key: `${hour}:30`,
        display: `${hour.toString().padStart(2, '0')}:30 - ${nextHour.toString().padStart(2, '0')}:00`,
        startTime: `${hour.toString().padStart(2, '0')}:30`,
        endTime: `${nextHour.toString().padStart(2, '0')}:00`
      });
    }
    
    return slots;
  };

  const timeSlots = generateTimeSlots();

  // Format date for API (YYYY-MM-DD)
  const formatDateForAPI = (date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedBranch && userRole !== 'superadmin') return;
      
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const formattedDate = formatDateForAPI(selectedDate);

        // Fetch classrooms - the API already handles role-based filtering
        let classroomUrl = `${API_URL}/classrooms`;
        if (userRole === 'superadmin' && selectedBranch) {
          classroomUrl = `${API_URL}/classrooms/by-branch/${selectedBranch}`;
        }

        // Fetch classes with branch filter for superadmin
        let classUrl = `${API_URL}/classes/by-date?date=${formattedDate}`;
        if (userRole === 'superadmin' && selectedBranch) {
          classUrl += `&branchId=${selectedBranch}`;
        }

        const [classroomResponse, classResponse] = await Promise.all([
          axios.get(classroomUrl, { 
            headers: { Authorization: `Bearer ${token}` } 
          }),
          axios.get(classUrl, { 
            headers: { Authorization: `Bearer ${token}` } 
          })
        ]);

        setClassrooms(classroomResponse.data);
        setClasses(classResponse.data);
        setError(null);

      } catch (error) {
        console.error('❌ Error fetching data:', error);
        console.error('❌ Error details:', {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data
        });
        message.error(t('classroom.fetch_error'));
        setError(t('classroom.fetch_error'));
      } finally {
        setLoading(false);
      }
    };

    if (userRole) {
      fetchData();
    }
  }, [API_URL, t, selectedDate, selectedBranch, userRole]);

  const formatDate = (date) => {
    const options = { 
      weekday: 'long',
      day: '2-digit', 
      month: 'long', 
      year: 'numeric' 
    };
    return new Intl.DateTimeFormat(i18n.language, options).format(date);
  };

  // Get classes for the selected date (now filtered by API)
  const getClassEventsForDay = (date) => {
    // Since API already filters by date, return all classes
    return classes;
  };

  // Parse time string to minutes
  const parseTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Find which time slots a class spans
  const findClassTimeSlots = (classStartTime, classEndTime) => {
    const startMinutes = parseTime(classStartTime);
    const endMinutes = parseTime(classEndTime);
    
    const spannedSlots = [];
    
    timeSlots.forEach((slot, index) => {
      const slotStartMinutes = parseTime(slot.startTime);
      const slotEndMinutes = parseTime(slot.endTime);
      
      // Check if class overlaps with this time slot
      if (startMinutes < slotEndMinutes && endMinutes > slotStartMinutes) {
        spannedSlots.push({
          index,
          slot: slot.key,
          isFirst: spannedSlots.length === 0
        });
      }
    });
    
    return spannedSlots;
  };

  // Sort classrooms by number
  const sortedClassrooms = [...classrooms].sort((a, b) => {
    const numA = a.number.match(/\d+/g);
    const numB = b.number.match(/\d+/g);
    return numA && numB ? parseInt(numA[0], 10) - parseInt(numB[0], 10) : a.number.localeCompare(b.number);
  });

  const renderDayScheduleTable = () => {
    const dayEvents = getClassEventsForDay(selectedDate);

    // Process classes and determine merged cells
    const mergedCells = new Map(); // Track which cells should be merged
    const cellData = new Map(); // Store class data for cells
    
    dayEvents.forEach(event => {
      const classTimeSlots = findClassTimeSlots(
        event.schedule_time.slice(0, 5),
        event.end_time.slice(0, 5)
      );
      
      if (classTimeSlots.length > 0) {
        const classData = {
          subject_name: event.subject_name,
          nickname: event.nickname,
          schedule_time: event.schedule_time.slice(0, 5),
          end_time: event.end_time.slice(0, 5),
          student_confirm: event.student_confirm,
          student_nicknames: event.student_nicknames,
          rowSpan: classTimeSlots.length
        };
        
        classTimeSlots.forEach(({ index, slot, isFirst }) => {
          const cellKey = `${index}-${event.classroom_number}`;
          
          if (isFirst) {
            // First cell contains the class data and rowspan
            cellData.set(cellKey, classData);
            mergedCells.set(cellKey, { rowSpan: classTimeSlots.length, colSpan: 1 });
          } else {
            // Subsequent cells are marked as merged (hidden)
            mergedCells.set(cellKey, { rowSpan: 0, colSpan: 0 });
          }
        });
      }
    });

    const truncateText = (text, maxLength = 25) => {
      if (!text) return '';
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    // Create time column + classroom columns
    const columns = [
      {
        title: t('time'),
        dataIndex: 'timeSlot',
        key: 'timeSlot',
        width: 100,
        fixed: 'left',
        className: 'col-time',
        onHeaderCell: () => ({ className: 'col-time' }),
        onCell: () => ({
          className: 'col-time',
          style: { 
            backgroundColor: '#fafafa', 
            fontWeight: 600, 
            textAlign: 'center', 
            verticalAlign: 'middle',
            width: '150px',
            minWidth: '150px',
            maxWidth: '150px',
            padding: '8px 4px'
          }
        }),
        render: (text) => <Text strong style={{ fontSize: 12 }}>{text}</Text>
      },
      ...sortedClassrooms.map(classroom => ({
        title: <div style={{ textAlign: 'center' }}><Text strong>{classroom.number}</Text></div>,
        dataIndex: classroom.number,
        key: classroom.number,
        width: 120,
        className: 'col-class',
        onHeaderCell: () => ({ className: 'col-class' }),
        onCell: (record, index) => {
          const cellKey = `${index}-${classroom.number}`;
          const merge = mergedCells.get(cellKey);
          const hasData = cellData.get(cellKey);
          const baseStyle = {
            width: '120px',
            minWidth: '120px',
            maxWidth: '120px',
            padding: '8px 4px',
            overflow: 'hidden',
            borderRadius: '2px',
            border: hasData ? '1px solid #52c41a' : '1px solid #d9d9d9',
            background: hasData 
              ? 'linear-gradient(135deg, rgba(125, 220, 192, 0.2) 0%, rgba(168, 230, 207, 0.2) 50%, rgba(244, 248, 187, 0.2) 100%)'
              : 'transparent',
          };
        
          return merge 
            ? { 
                rowSpan: merge.rowSpan, 
                colSpan: merge.colSpan, 
                className: 'col-class',
                style: baseStyle
              } 
            : { 
                className: 'col-class',
                style: baseStyle
              };
        },
        render: (text, record, index) => {
          const cellKey = `${index}-${classroom.number}`;
          const classData = cellData.get(cellKey);
          
          if (!classData) return null;
          
          // Combined tooltip content
          const tooltipContent = (
            <div style={{ maxWidth: '300px' }}>
              <div><strong>Subject:</strong> {classData.subject_name}</div>
              <div><strong>Time:</strong> {classData.schedule_time} - {classData.end_time}</div>
              <div><strong>Status:</strong> {classData.student_confirm ? '✅ Confirmed' : '❌ Not Confirmed'}</div>
              <div><strong>Teacher:</strong> {classData.nickname}</div>
              <div><strong>Students:</strong> {classData.student_nicknames}</div>
            </div>
          );
          
          return (
            <Tooltip title={tooltipContent} placement="top" mouseEnterDelay={0.3}>
              <div className="class-cell" style={{ cursor: 'pointer' }}>
                <div>
                  <Text style={{ fontSize: '12px', color: '#1890ff' }}>
                    {truncateText(classData.subject_name, 15)}
                  </Text>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {classData.schedule_time} - {classData.end_time} {classData.student_confirm ? '✅' : '❌'}
                  </Text>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    🧑‍🏫 {truncateText(classData.nickname, 12)}
                  </Text>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    🧑‍🎓 {truncateText(classData.student_nicknames, 12)}
                  </Text>
                </div>
              </div>
            </Tooltip>
          );
        },
      }))
    ];

    // Create data source for the table (time slots as rows)
    const dataSource = timeSlots.map((timeSlot, index) => {
      const rowData = {
        key: timeSlot.key,
        timeSlot: timeSlot.display
      };

      // Initialize all classroom columns
      sortedClassrooms.forEach(classroom => {
        rowData[classroom.number] = null;
      });

      return rowData;
    });

    return (
      <div className="day-schedule-table">
        <Table 
          dataSource={dataSource} 
          columns={columns} 
          pagination={false}
          bordered
          size="small"
          scroll={{ x: '100%', y: 'calc(100vh - 200px)' }}
          className="schedule-table"
          rowClassName={(record, index) => 
            index % 2 === 0 ? 'even-time-row' : 'odd-time-row'
          }
          sticky={{
            offsetHeader: 0,
          }}
        />
      </div>
    );
  };

  const handlePreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  const handleNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    setSelectedDate(newDate);
  };

  const handleToday = () => {
    setSelectedDate(new Date());
  };

  const isToday = () => {
    const today = new Date();
    const selected = new Date(selectedDate);
    return today.toDateString() === selected.toDateString();
  };

  const handleBranchChange = (branchId) => {
    setSelectedBranch(branchId);
  };

  if (loading) return <Spin tip={t('loading')} size="large" style={{ 
    position: 'fixed', 
    top: '50%', 
    left: '50%', 
    transform: 'translate(-50%, -50%)' 
  }} />;
  
  if (error) return (
    <div style={{ textAlign: 'center', marginTop: '50px' }}>
      <Text type="danger" style={{ fontSize: '16px' }}>{error}</Text>
    </div>
  );

  return (
    <div>
      <Sidebar />
      <Header />
      <div className="classroom-page-container">



        {/* Date Navigation */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          gap: '16px', 
          marginBottom: '24px',
          background: '#fafafa',
          padding: '16px',
          borderRadius: '8px'
        }}>
          {userRole === 'superadmin' && (
          <div>
            <Text strong style={{ marginRight: '12px' }}>
              {t('Select Branch')}:
            </Text>
            <Select
              style={{ minWidth: '200px' }}
              value={selectedBranch}
              onChange={handleBranchChange}
              placeholder={t('Select a branch')}
            >
              {branches.map(branch => (
                <Option key={branch.id} value={branch.id}>
                  {branch.name}
                </Option>
              ))}
            </Select>
          </div>
        )}
          <Button
            onClick={handlePreviousDay}
            icon={<LeftOutlined />}
            style={{ fontSize: '16px' }}
          >
            {t('Yesterday')}
          </Button>
          
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            minWidth: '300px'
          }}>
            <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
              {formatDate(selectedDate)}
            </Title>
            {!isToday() && (
              <Button
                type="link"
                onClick={handleToday}
                style={{ padding: '0', height: 'auto', fontSize: '14px' }}
              >
                {t('Go To Today')}
              </Button>
            )}
          </div>
          
          <Button
            onClick={handleNextDay}
            icon={<RightOutlined />}
            iconPosition="end"
            style={{ fontSize: '16px' }}
          >
            {t('Tomorrow')}
          </Button>
        </div>

        {/* Schedule Table */}
        <div style={{ 
          background: 'white', 
          borderRadius: '8px', 
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          position: 'relative'
        }}>
          {loading ? (
            <div style={{ 
              padding: '40px', 
              textAlign: 'center',
              minHeight: '200px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Spin size="large" tip={t('loading')} />
            </div>
          ) : (
            renderDayScheduleTable()
          )}
        </div>
      </div>
    </div>
  );
}

export default withRoleAccess(Classroom, ['admin', 'superadmin']);